/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IconButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { DirectionalHint, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { Image } from '@fluentui/react/lib/Image';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import icon from '../../../assets/icon-color.png';
import { Paths } from '../../../common/enums/Paths';
import { useSetModalDataMutation } from '../../../data/client/mutations/SetModalData.generated';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import { useModalDataQuery } from '../../../data/client/queries/ModalData.generated';
import { ApprovalRequestType, ModalComponentType } from '../../../data/types.generated';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../logger';
import { getDefaultIconStyles } from '../../../styles/custom/IconStyles';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { useShowTemplateManagementEntry } from '../../../utilities/TemplateManagementUtils';
import { CoachMark, useCoachMarkState } from '../../../utilities/TemplateUtils';
import { getAppName } from '../../../utilities/Utils';
import { CoachMark as EsignCoachMark } from '../../Esign/CoachMark';
import { ApprovalsUserRoleSelectorContainer } from '../ApprovalsRoleSelector/ApprovalsRoleSelector.container';
import {
    getLabelStyles,
    getMoreButtonStyles,
    getNewApprovalButtonStyles,
    getStackStyles,
    getTeachingBubbleStyles,
} from './ApprovalsAppHeader.styles';

export const NEW_APPROVAL_BUTTON_ID = 'new-approval-button';

export const ApprovalsAppHeader: React.FC = (): JSX.Element => {
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const { data: listData } = useListDataQuery();
    const { data: modalData } = useModalDataQuery();
    const [setModalData] = useSetModalDataMutation();

    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();
    const [showTemplateCoachMark, hideTemplateCoachMark] = useCoachMarkState(CoachMark.TemplateManagementEntry);
    const [showDataExportCoachMark] = useCoachMarkState(CoachMark.DataExport);
    const [shouldDismissEsignCoachMark, setShouldDismissEsignCoachMark] = React.useState(false);
    const showTemplateManagementEntry = useShowTemplateManagementEntry();
    const teamApprovalEcsConfig = host.getEcsConfig().MicrosoftTeamApprovals;
    const isNewTemplateManagementButtonEnabled = teamApprovalEcsConfig.NewTemplateManagementButtonEnabled;

    const history = useHistory();
    const approvalType = listData.listData.approvalType;
    const isNewApprovalButtonDisabled =
        approvalType !== ApprovalRequestType.Basic &&
        approvalType !== ApprovalRequestType.ESign &&
        approvalType !== ApprovalRequestType.Docusign;

    let moreButton = null;
    const shouldRenderMoreButton = !isNewTemplateManagementButtonEnabled && showTemplateManagementEntry;
    const isEsignFeatureEnabled = teamApprovalEcsConfig.AdobeEnabled || teamApprovalEcsConfig.DocusignEnabled;
    const shouldTryToDisplayEsignCoachMark =
        isEsignFeatureEnabled &&
        !modalData?.modalData.isOpen &&
        !(showTemplateCoachMark && shouldRenderMoreButton) &&
        !showDataExportCoachMark;
    // If the user is an admin or owner of any team, show template management entry button.
    if (shouldRenderMoreButton) {
        const moreButtonItems: IContextualMenuProps = {
            onMenuOpened: hideTemplateCoachMark,
            items: [
                {
                    key: 'templateManagement',
                    text: loc.strings.manage_template_text,
                    iconProps: { iconName: 'Settings' },
                    onClick: (): void => {
                        logger.markScenario(
                            ScenarioName.PersonalApp,
                            StepName.OpenTemplateManagementPage,
                            StatusName.NA
                        );
                        logger.logUserBiAction(
                            SubWorkLoad.AppInteraction,
                            ScenarioName.PersonalApp,
                            StepName.OpenTemplateManagementPage,
                            'click',
                            'nav',
                            'templateManagement',
                            'templateManagementButton',
                            PanelRegion.Main,
                            PanelType.Approvals,
                            {
                                ApprovalType: approvalType,
                            }
                        );

                        hideTemplateCoachMark();
                        history.push({
                            pathname: Paths.BasePath + Paths.TemplateManagement,
                        });
                    },
                    ariaLabel: loc.strings.manage_template_text,
                },
            ],
            alignTargetEdge: true,
        };
        moreButton = (
            <>
                <IconButton
                    id='more-button'
                    styles={getMoreButtonStyles(theme)}
                    menuProps={moreButtonItems}
                    iconProps={{ iconName: 'More' }}
                    ariaLabel={loc.strings.more_button_aria_label}
                    title={loc.strings.more_button_aria_label}
                />
                {showTemplateCoachMark && (
                    <TeachingBubble
                        calloutProps={{
                            calloutWidth: 280,
                            directionalHint: DirectionalHint.bottomCenter,
                        }}
                        target='#more-button'
                        styles={getTeachingBubbleStyles}
                        onDismiss={hideTemplateCoachMark}
                        secondaryButtonProps={{
                            children: loc.strings.got_it_button_text,
                            onClick: hideTemplateCoachMark,
                        }}
                        headline={loc.strings.create_template_coach_mark_header}>
                        {loc.strings.create_template_coach_mark_content}
                    </TeachingBubble>
                )}
            </>
        );
    }

    const onNewApprovalButtonClick = (): void => {
        openModal();
        setShouldDismissEsignCoachMark(true);
    };

    const openModal = React.useCallback((): void => {
        logger.markScenario(ScenarioName.PersonalApp, StepName.OpenCreateApprovalModule, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.PersonalApp,
            StepName.OpenCreateApprovalModule,
            'click',
            'nav',
            'newApproval',
            'newApprovalButton',
            PanelRegion.Main,
            PanelType.Approvals,
            {
                ApprovalType: approvalType,
            }
        );

        setModalData({
            variables: {
                isOpen: true,
                componentType: ModalComponentType.CreateTaskModule,
                componentProps: null,
            },
        });
    }, [logger, setModalData]);

    const userRoleSelectorTabs = (
        <Stack.Item align='center' className='approvals-app-user-role-selector-tab'>
            <ApprovalsUserRoleSelectorContainer />
        </Stack.Item>
    );

    const newApprovalButton = (
        <PrimaryButton
            primary
            iconProps={{
                iconName: 'Add',
            }}
            id={NEW_APPROVAL_BUTTON_ID}
            onClick={onNewApprovalButtonClick}
            ariaLabel={loc.strings.new_approval_button_text}
            disabled={isNewApprovalButtonDisabled}
            styles={getNewApprovalButtonStyles(theme)}>
            {loc.strings.new_approval_button_text}
        </PrimaryButton>
    );

    return (
        <>
            <Stack horizontal disableShrink horizontalAlign='space-between' styles={getStackStyles(theme)}>
                <Stack.Item align='center'>
                    <Stack horizontal>
                        <Stack.Item align='center'>
                            <Image
                                src={icon}
                                height={32}
                                width={32}
                                styles={getDefaultIconStyles()}
                                title={loc.strings.approvals_icon_title}
                                aria-label={loc.strings.approvals_icon_title}
                            />
                        </Stack.Item>
                        <Stack.Item grow align='center'>
                            <Label styles={getLabelStyles(theme)}>{getAppName(loc)}</Label>
                        </Stack.Item>
                        {userRoleSelectorTabs}
                    </Stack>
                </Stack.Item>
                <Stack.Item align='center'>
                    <Stack horizontal tokens={{ childrenGap: '0.75rem' }}>
                        {newApprovalButton}
                        {moreButton}
                    </Stack>
                </Stack.Item>
            </Stack>
            {shouldTryToDisplayEsignCoachMark && <EsignCoachMark shouldDismiss={shouldDismissEsignCoachMark} />}
        </>
    );
};
